import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useCustomerView } from "@/context/CustomerViewContext";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { formatApiErrorDetail } from "@/lib/errors";
import { Loader2, Package, Search, Sparkles, X } from "lucide-react";
import { BackToTop } from "@/components/BackToTop";
import { NotifyMeDialog, useMyNotifyMe } from "@/components/NotifyMe";
import { CatalogCard, FORMAT_CHIPS } from "@/components/dealer/CatalogCard";

const TABS = [["zellige", "Zellige Tiles"], ["mosaic", "Mosaic Collections"]];
const Grid = ({ children }) => <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">{children}</div>;

function Chip({ active, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`rounded-full border px-4 py-1.5 text-sm transition-colors duration-200 ${active ? "border-terracotta bg-terracotta text-white" : "border-border bg-white text-charcoal hover:border-terracotta hover:text-terracotta"}`}>
      {children}
    </button>
  );
}

export default function Discover() {
  const [items, setItems] = useState(null);
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [tab, setTab] = useState("zellige");
  const [fmt, setFmt] = useState("all");
  const [stockOnly, setStockOnly] = useState(false);
  const { customerView } = useCustomerView();
  const navigate = useNavigate();
  const debounceRef = useRef(null);
  const notifyMe = useMyNotifyMe();
  const [notifyTarget, setNotifyTarget] = useState(null);
  const cv = customerView ? "customer_view=1" : "";

  const runSearch = (q) => {
    const url = q.trim() ? `/catalog/search?q=${encodeURIComponent(q)}${cv ? `&${cv}` : ""}` : `/catalog${cv ? `?${cv}` : ""}`;
    setSearching(!!q.trim());
    api.get(url).then((res) => setItems(res.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setSearching(false));
  };

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(query), 350);
    return () => clearTimeout(debounceRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, customerView]);

  const counts = useMemo(() => ({ zellige: (items || []).filter((v) => v.category !== "mosaic").length, mosaic: (items || []).filter((v) => v.category === "mosaic").length }), [items]);
  useEffect(() => {
    if (query && items && counts[tab] === 0 && counts[tab === "zellige" ? "mosaic" : "zellige"] > 0) { setTab(tab === "zellige" ? "mosaic" : "zellige"); }
  }, [items]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!items) {
    return <div className="flex min-h-[40vh] items-center justify-center" data-testid="catalog-loading"><Loader2 className="h-6 w-6 animate-spin text-terracotta" /></div>;
  }
  const shown = items.filter((v) => (tab === "mosaic" ? v.category === "mosaic" : v.category !== "mosaic"))
    .filter((v) => tab !== "zellige" || fmt === "all" || v.format_in === fmt)
    .filter((v) => !stockOnly || v.florida_available > 0);
  const collections = [...new Set(shown.filter((v) => v.category === "mosaic").map((v) => v.collection))];
  const card = (v) => <CatalogCard key={v.id} v={v} customerView={customerView} notifyMe={notifyMe} onNotify={setNotifyTarget} onOpen={() => navigate(`/dealer/discover/${v.id}`)} />;

  return (
    <div data-testid="dealer-discover-page">
      <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Catalog</p>
      <h1 className="mt-3 font-display text-4xl font-light tracking-tight text-charcoal md:text-5xl">Products</h1>
      <p className="mt-3 text-sm text-warmgrey">{customerView ? "Customer View — trade pricing and margins are hidden." : "Trade pricing and your margins are visible. Toggle Customer View when a client is present."}</p>

      <div className="mt-8 flex flex-wrap gap-2 border-b border-border" role="tablist">
        {TABS.map(([id, label]) => (
          <button key={id} role="tab" aria-selected={tab === id} onClick={() => setTab(id)} data-testid={`catalog-tab-${id}`}
            className={`-mb-px border-b-2 px-4 py-3 text-sm font-medium transition-colors ${tab === id ? "border-terracotta text-charcoal" : "border-transparent text-warmgrey hover:text-charcoal"}`}>
            {label} <span className="ml-1 text-xs text-muted-foreground" data-testid={`catalog-tab-count-${id}`}>{counts[id]}</span>
          </button>
        ))}
      </div>

      <div className="mt-6 flex flex-col gap-4 lg:flex-row lg:items-center">
        <div className="relative w-full max-w-xl">
          <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder='SKU, color, format or collection — e.g. "MZ001", "Mocha", "2x6", "DAMA"'
            data-testid="catalog-search-input" className="h-12 border-border bg-white pl-11 pr-10 focus-visible:ring-terracotta" />
          {query && <button onClick={() => setQuery("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-charcoal" data-testid="catalog-search-clear" aria-label="Clear search"><X className="h-4 w-4" /></button>}
        </div>
        <div className="flex flex-wrap gap-2" data-testid="catalog-filters">
          {tab === "zellige" && [["all", "All formats"], ...FORMAT_CHIPS].map(([id, label]) => <Chip key={id} active={fmt === id} onClick={() => setFmt(id)} testid={`format-chip-${id}`}>{label}</Chip>)}
          <Chip active={stockOnly} onClick={() => setStockOnly(!stockOnly)} testid="filter-in-stock">In stock in Florida</Chip>
        </div>
      </div>
      {searching && <p className="mt-2 text-xs text-muted-foreground">Searching…</p>}
      {query && !searching && <p className="mt-2 text-xs text-muted-foreground" data-testid="search-result-count">{items.length} result{items.length === 1 ? "" : "s"} for “{query}”</p>}

      {shown.length === 0 ? (
        <Card className="mt-10 border-border bg-white"><CardContent className="flex flex-col items-center p-16 text-center">
          <Package className="h-8 w-8 text-muted-foreground" />
          <p className="mt-4 text-sm text-muted-foreground" data-testid="catalog-empty">
            {query ? "No products match this search — try different terms or clear the search." : tab === "mosaic" ? "Mosaic collections are coming soon." : "Catalog is being prepared. Check back soon."}
          </p>
        </CardContent></Card>
      ) : tab === "mosaic" ? (
        collections.map((c) => (
          <section key={c} className="mt-10" data-testid={`collection-${c}`}>
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div><p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-terracotta">Collection</p><h2 className="font-display text-2xl font-light text-charcoal">{c}</h2></div>
              <Button variant="outline" size="sm" onClick={() => navigate(`/dealer/custom-studio?design_id=${encodeURIComponent(shown.find((v) => v.collection === c)?.design_id || "")}`)} data-testid={`request-colors-${c}`} className="gap-1.5 border-terracotta text-terracotta hover:bg-terracotta hover:text-white">
                <Sparkles className="h-3.5 w-3.5" /> Request other colors
              </Button>
            </div>
            <Grid>{shown.filter((v) => v.collection === c).map(card)}</Grid>
          </section>
        ))
      ) : <Grid>{shown.map(card)}</Grid>}
      <NotifyMeDialog variant={notifyTarget} open={!!notifyTarget} onOpenChange={(o) => !o && setNotifyTarget(null)} onSubmitted={() => { if (notifyTarget) notifyMe.add(notifyTarget.id); }} />
      <BackToTop />
    </div>
  );
}
